import { Controller } from '@hotwired/stimulus';

const COLORS = ['#155893', '#3FAAE1', '#1A2476', '#DFEEF8', '#EFEFEF'];

export default class DoughnutChartController extends Controller {
  static targets = ['canvas', 'legend'];
  static values = { data: Object };

  connect() {
    if (!this.hasCanvasTarget) return;

    this.element.style.position = 'relative';
    this.tooltipEl = this.#createTooltipElement();
    this.element.appendChild(this.tooltipEl);

    const data = this.dataValue;
    data.datasets[0].backgroundColor = COLORS;
    data.datasets[0].borderWidth = 0;
    data.datasets[0].hoverOffset = 0;

    this.chart = new Chart(this.canvasTarget, {
      type: 'doughnut',
      data: data,
      options: {
        maintainAspectRatio: false,
        cutout: '75%',
        responsive: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: this.#renderTooltip.bind(this),
          },
        },
      },
    });

    this.#buildLegend(data);
  }

  disconnect() {
    this.chart?.destroy();
    this.chart = null;
    this.tooltipEl?.remove();
    this.tooltipEl = null;
  }

  #createTooltipElement() {
    const el = document.createElement('div');
    Object.assign(el.style, {
      position: 'absolute',
      pointerEvents: 'none',
      opacity: '0',
      transition: 'opacity 0.15s ease',
      backgroundColor: '#ffffff',
      color: '#262626',
      border: '1px solid #E3F0FB',
      borderRadius: '8px',
      padding: '12px',
      fontSize: '14px',
      whiteSpace: 'nowrap',
      zIndex: '1000',
      transform: 'translate(-50%, -100%)',
    });
    return el;
  }

  #renderTooltip(context) {
    const { tooltip } = context;

    if (tooltip.opacity === 0) {
      this.tooltipEl.style.opacity = '0';
      return;
    }

    this.tooltipEl.replaceChildren();

    (tooltip.title || []).forEach((title) => {
      const t = document.createElement('div');
      t.style.fontWeight = 'bold';
      t.textContent = title;
      this.tooltipEl.appendChild(t);
    });

    tooltip.body.forEach((body, i) => {
      const colors = tooltip.labelColors[i];
      const wrapper = document.createElement('div');
      wrapper.style.display = 'flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.gap = '8px';

      const dot = document.createElement('span');
      Object.assign(dot.style, {
        display: 'inline-block',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: colors.backgroundColor,
        flexShrink: '0',
      });

      const text = document.createElement('span');
      text.textContent = body.lines.join(' ');

      wrapper.append(dot, text);
      this.tooltipEl.appendChild(wrapper);
    });

    const canvasRect = this.canvasTarget.getBoundingClientRect();
    const containerRect = this.element.getBoundingClientRect();
    const offsetX = canvasRect.left - containerRect.left;
    const offsetY = canvasRect.top - containerRect.top;

    this.tooltipEl.style.opacity = '1';
    this.tooltipEl.style.left = `${offsetX + tooltip.caretX}px`;
    this.tooltipEl.style.top = `${offsetY + tooltip.caretY}px`;
  }

  #buildLegend(data) {
    if (!this.hasLegendTarget) return;

    this.legendTarget.replaceChildren();

    data.labels.forEach((label, i) => {
      const item = document.createElement('div');
      item.className = 'd-flex align-items-center gap-2';

      const dot = document.createElement('span');
      dot.className = 'd-inline-block rounded-circle flex-shrink-0';
      dot.style.width = '8px';
      dot.style.height = '8px';
      dot.style.backgroundColor = COLORS[i] || '#EFEFEF';

      const text = document.createElement('span');
      text.className = 'fs-sm';
      text.textContent = label;

      item.append(dot, text);
      this.legendTarget.append(item);
    });
  }
}
